import React, { FC, useEffect, useMemo, useState } from 'react';
import styles from './index.module.scss';
import { CloseOutlined, LoadingOutlined } from '@ant-design/icons';
import { Form, Modal as AntModal, Spin } from 'antd';
import { useRequest } from 'ahooks';
import { Button, Empty, Modal, Paging, Select, Switch, useSearchParams, useUpdate } from '@maxtropy/components';
import {
  apiV2GgdBatchOperateStatusPost,
  apiV2LightmapCircuitDeviceListPost,
  apiV2LightmapCircuitDevicePagePost,
} from '@maxtropy/device-customer-apis-v2';
import { StatusType } from '@/pages/PowerLightingControl/utils';
import { keepTwoOrLine } from '@/utils/utils';

export interface IWorkshopPanel {
  zoneName?: string;
  workSpaceLightVisible: boolean;
}

const WorkshopPanel: FC<IWorkshopPanel> = props => {
  const { zoneName, workSpaceLightVisible } = props;
  const [deviceId, setDeviceId] = useState<number>();
  const [update, updateFn] = useUpdate();
  const pagingInfo = useSearchParams(50);
  const { pageOffset, pageSize, setTotalCount } = pagingInfo;
  const [modalApi, modalContextHolder] = AntModal.useModal();

  const { data: deviceList } = useRequest(
    async () => {
      // 与用气大屏一致：点击区域后按区域名称（zoneName）查询设备列表
      const res = await apiV2LightmapCircuitDeviceListPost({ regionName: zoneName });
      return res.list;
    },
    {
      ready: workSpaceLightVisible,
      refreshDeps: [zoneName, workSpaceLightVisible],
    }
  );

  const { data, loading } = useRequest(
    () =>
      apiV2LightmapCircuitDevicePagePost({
        deviceIds: deviceId ? [deviceId] : undefined,
        regionName: zoneName,
        page: pageOffset,
        size: pageSize,
      }).then(res => {
        setTotalCount(res.total ?? 0);
        return res.list;
      }),
    {
      ready: workSpaceLightVisible,
      refreshDeps: [deviceId, pageOffset, pageSize, update, workSpaceLightVisible, zoneName],
    }
  );

  // 弹窗关闭，清空搜索框
  useEffect(() => {
    if (workSpaceLightVisible) return;
    setDeviceId(undefined);
  }, [workSpaceLightVisible]);

  const deviceOptions = useMemo(() => {
    return (deviceList ?? []).map(item => ({
      label: item.deviceName,
      value: item.deviceId,
    }));
  }, [deviceList]);

  const makeLoadingModel = () =>
    modalApi.info({
      className: 'loading-modal',
      title: '正在执行',
      content: (
        <div className="loading-modal-content">
          <div className="loading-modal-icon">
            <LoadingOutlined />
          </div>
          <div className="loading-modal-txt">正在执行中</div>
        </div>
      ),
    });

  const changeStatus = ({ status, name, deviceId }: { name?: string; deviceId?: number; status: StatusType }) => {
    Modal.confirm({
      title: (
        <span style={{ color: 'var(--mx-text-base-color)' }}>
          确定要下发<span style={{ color: '#E64242' }}>{status === StatusType.CLOSED ? '远程开启' : '远程关闭'}</span>
          指令到{name}设备吗？
        </span>
      ),
      okText: '确定',

      onOk() {
        const modal = makeLoadingModel();
        apiV2GgdBatchOperateStatusPost({ deviceIds: [deviceId!], status })
          .then(_ => {
            updateFn();
          })
          .finally(() => {
            modal.destroy();
          });
      },
    });
  };

  return (
    <div className={styles.panel}>
      <div className={styles.titleContent}>
        <div className={styles.title}>{zoneName ?? '--'}</div>
        {/* <CloseOutlined style={{ fontSize: '20px' }} /> */}
      </div>
      <div className={styles.content}>
        <Form.Item label="控制设备名称" style={{ margin: '0 0 16px' }}>
          <Select
            placeholder="请选择"
            optionFilterProp="label"
            showSearch
            allowClear
            options={deviceOptions}
            onChange={(value: number) => {
              setDeviceId(value);
            }}
            style={{ width: 280 }}
          />
        </Form.Item>
        <Spin spinning={loading}>
          {data && data.length > 0 ? (
            <>
              <div className={styles.card}>
                {data?.map(item => (
                  <div className={styles.cardItem}>
                    <div className={styles.cardTitleContent}>
                      <span className={styles.cardTitle}>{item.name}</span>
                      <Button
                        type="link"
                        style={{ padding: 0 }}
                        onClick={() => {
                          window.open(`/power/lighting/control`, '_blank');
                        }}
                      >
                        更多{'>>'}
                      </Button>
                    </div>
                    <div className={styles.cardInfo}>
                      <div className={styles.power}>
                        <div>
                          功率：<b style={{ color: 'var(--mx-diagram1-color)' }}>{keepTwoOrLine(item.power)}w</b>
                        </div>
                        <div>
                          状态：
                          {item.intStatus === 2 ? (
                            <span style={{ color: '#FF9247' }}>离线</span>
                          ) : (
                            <Switch
                              checkedChildren="开"
                              unCheckedChildren="关"
                              checked={item.intStatus === 1}
                              onClick={checked =>
                                changeStatus({
                                  status: checked ? StatusType.CLOSED : StatusType.OPEN,
                                  name: item.name,
                                  deviceId: item.deviceId,
                                })
                              }
                            />
                          )}
                        </div>
                      </div>
                      <div>
                        温度：<b>{keepTwoOrLine(item.temperature)}℃</b>
                      </div>
                      <div>
                        A/B/C三相电压：
                        <b>
                          <span style={{ color: 'var(--mx-warning-color)' }}>{keepTwoOrLine(item.avoltage)}</span>/
                          <span style={{ color: 'var(--mx-success-color)' }}>{keepTwoOrLine(item.bvoltage)}</span>/
                          <span style={{ color: 'var(--mx-error-color)' }}>{keepTwoOrLine(item.cvoltage)}</span> V
                        </b>
                      </div>
                      <div>
                        A/B/C三相电流：
                        <b>
                          <span style={{ color: 'var(--mx-warning-color)' }}>{keepTwoOrLine(item.acurrent)}</span>/
                          <span style={{ color: 'var(--mx-success-color)' }}>{keepTwoOrLine(item.bcurrent)}</span>/
                          <span style={{ color: 'var(--mx-error-color)' }}>{keepTwoOrLine(item.ccurrent)}</span> A
                        </b>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              <Paging pagingInfo={pagingInfo} className={styles.paging} />
            </>
          ) : (
            <div className={styles.empty}>
              <Empty />
            </div>
          )}
        </Spin>
      </div>
      {modalContextHolder}
    </div>
  );
};
export default WorkshopPanel;
